import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { BottomNav } from './BottomNav';
import { DesktopNav } from './DesktopNav';
import { getBottomTabsForRole, getAllTabsForRole } from './roleTabs';
import { OfflineBanner } from '../components/ui/OfflineBanner';
import { SubscriptionExpiryBanner } from '../components/ui/SubscriptionExpiryBanner';
import { PushNotificationSetup } from '../components/PushNotificationSetup';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { AppLogoIcon } from '../components/ui/AppLogo';
import { TeacherDashboard } from '../pages/TeacherDashboard';
import { HeadDashboard } from '../pages/HeadDashboard';
import { AdminDashboard } from '../pages/AdminDashboard';

const defaultTab = { teacher: 'home', head: 'report', admin: 'schools' };

export function AppShell() {
  const app = useApp();
  const {
    role,
    labels,
    token,
    logout,
    setUseMalayalam,
    useMalayalam,
    online,
    pending,
    syncing,
    syncNow,
    lastSyncError,
    userInfo,
  } = app;
  const showSubscriptionWarn =
    (role === 'head' || role === 'teacher') && userInfo?.subscription_expires_at;
  const [activeTab, setActiveTab] = useState(defaultTab[role] || 'home');
  const teacherClassCount = app.teacherClasses?.length ?? 0;
  const navTabs =
    role === 'teacher'
      ? {
          bottom: getBottomTabsForRole('teacher', { teacherClassCount }),
          all: getAllTabsForRole('teacher', { teacherClassCount }),
        }
      : {
          bottom: getBottomTabsForRole(role),
          all: getAllTabsForRole(role),
        };

  useEffect(() => {
    setActiveTab(defaultTab[role] || 'home');
  }, [role]);

  useEffect(() => {
    if (role === 'teacher' && activeTab === 'switchClass' && teacherClassCount <= 1) {
      setActiveTab('home');
    }
  }, [role, activeTab, teacherClassCount]);

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-40 border-b border-border bg-card/90 pt-safe shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <AppLogoIcon className="h-11 w-11 shrink-0 object-contain sm:h-12 sm:w-12" />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-text leading-tight">{labels.appTitle}</h1>
              {app.userInfo?.name && (
                <p className="text-xs text-muted truncate">{app.userInfo.name}</p>
              )}
            </div>
          </div>
          <div className="flex max-w-full flex-wrap items-center justify-end gap-2 sm:gap-3">
            <div className="hidden shrink-0 lg:flex lg:items-center">
              <ThemeToggle useMalayalam={useMalayalam} compact />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="hidden whitespace-normal text-center leading-tight lg:inline-flex"
              onClick={() => setUseMalayalam(!useMalayalam)}
            >
              {labels.toggleLang}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="whitespace-normal text-center leading-tight"
              onClick={logout}
            >
              {labels.signOut}
            </Button>
          </div>
        </div>
      </header>

      <DesktopNav
        role={role}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        labels={labels}
        tabs={navTabs.all}
      />

      <main className="mx-auto max-w-6xl px-4 py-4 pb-nav lg:pb-8">
        <PushNotificationSetup
          role={role}
          useMalayalam={useMalayalam}
          hasToken={Boolean(token)}
        />
        <div className="mb-4 space-y-3">
          {showSubscriptionWarn && (
            <SubscriptionExpiryBanner
              expiresAt={userInfo.subscription_expires_at}
              labels={labels}
              role={role}
            />
          )}
          <OfflineBanner
            online={online}
            pending={pending}
            syncing={syncing}
            onSync={() => syncNow()}
            lastSyncError={lastSyncError}
            labels={labels}
          />
        </div>
        {app.statusMessage && (
          <div className="mb-4 rounded-lg border border-primary/20 bg-primary-light px-4 py-3 text-sm text-primary">
            {app.statusMessage}
          </div>
        )}

        {role === 'teacher' && (
          <TeacherDashboard
            activeTab={activeTab}
            onNavigate={setActiveTab}
            hasMultipleClasses={teacherClassCount > 1}
          />
        )}
        {role === 'head' && (
          <HeadDashboard activeTab={activeTab} onNavigate={setActiveTab} />
        )}
        {role === 'admin' && <AdminDashboard activeTab={activeTab} />}
      </main>

      {token && (
        <BottomNav
          role={role}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          labels={labels}
          tabs={navTabs.bottom}
        />
      )}
    </div>
  );
}
